import { useField } from "@rvf/react-router";
import classNames from "classnames";
import { useState, type ReactNode } from "react";
import { type ErrorMessageProps } from "~/components/common/types";
import { Details } from "~/components/content/Details";
import { getGeldEinklagenTextareaRows } from "~/domains/geldEinklagen/formular/klage-erstellen/longTextFieldConfig";
import { TEXTAREA_CHAR_LIMIT } from "~/services/validation/inputlimits";
import InputError from "../error/InputError";
import { InputHelperText } from "../helperText/InputHelperText";
import { InputLabel } from "../label/InputLabel";

export const TEXT_AREA_ROWS = 3;

type TextareaProps = Readonly<{
  name: string;
  label?: ReactNode;
  details?: {
    title: string;
    content: string;
  };
  suffix?: string;
  innerRef?: React.Ref<HTMLTextAreaElement>;
  maxLength?: number;
  helperText?: string;
  placeholder?: string;
  errorMessages?: ErrorMessageProps[];
  backgroundClass?: string;
}>;

const Textarea = ({
  name,
  label,
  suffix,
  details,
  innerRef,
  helperText,
  placeholder,
  errorMessages,
  backgroundClass,
  maxLength = TEXTAREA_CHAR_LIMIT,
}: TextareaProps) => {
  const field = useField(name);
  const errorId = `${name}-error`;
  const helperId = `${name}-helper`;

  const [detailsId, setDetailsId] = useState<string | undefined>();

  const describedByIds = [
    field.error() ? errorId : null,
    helperText ? helperId : null,
    detailsId,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classNames("kern-form-input gap-kern-space-small!", {
        "kern-form-input--error": field.error(),
      })}
    >
      {(label || helperText) && (
        <div>
          {label && <InputLabel name={name} label={label} suffix={suffix} />}

          {helperText && (
            <InputHelperText helperText={helperText} helperId={helperId} />
          )}
        </div>
      )}

      {details && <Details {...details} setDetailsId={setDetailsId} />}

      <textarea
        {...field.getInputProps({
          id: name,
          placeholder,
        })}
        maxLength={maxLength}
        rows={getGeldEinklagenTextareaRows(name) ?? TEXT_AREA_ROWS}
        className={classNames(
          "kern-form-input__input ph-no-capture",
          {
            "kern-form-input__input--error": field.error(),
            "bg-white!": !backgroundClass,
          },
          backgroundClass,
        )}
        ref={innerRef}
        aria-invalid={field.error() !== null}
        aria-describedby={describedByIds || undefined}
        aria-errormessage={field.error() ? errorId : undefined}
        aria-required={!!errorMessages?.find((err) => err.code === "required")}
      />
      <InputError id={errorId}>
        {errorMessages?.find((err) => err.code === field.error())?.text ??
          field.error()}
      </InputError>
    </div>
  );
};

export default Textarea;
